import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Mail, Clock, User, CheckCircle, XCircle, AlertCircle, FileText, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

const API = '/api';

// Communication type labels
const COMMUNICATION_TYPE_LABELS = {
  application_received: 'Application Received',
  talent_pool_joined: 'Talent Pool Joined',
  screening_note: 'Screening Note',
  rejection: 'Rejection',
  shortlist: 'Shortlisted',
  interview_invitation: 'Interview Invitation',
  next_steps: 'Next Steps',
  follow_up: 'Follow-up',
  offer: 'Job Offer',
  training_inquiry_reply: 'Training Inquiry Reply',
  enrollment_follow_up: 'Enrollment Follow-up',
  training_confirmation: 'Training Confirmation',
  tna_reminder: 'TNA Reminder',
  training_general: 'Training Communication',
  inquiry_received: 'Inquiry Received',
  inquiry_reply: 'Inquiry Reply',
  general: 'General Communication',
};

// Status badge colors
const STATUS_COLORS = {
  sent: 'bg-green-100 text-green-800',
  mocked: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-800',
  draft: 'bg-gray-100 text-gray-800',
  internal: 'bg-purple-100 text-purple-800',
};

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'sent':
    case 'mocked':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'internal':
      return <FileText className="h-4 w-4 text-purple-500" />;
    default:
      return <AlertCircle className="h-4 w-4 text-gray-500" />;
  }
};

const CommunicationItem = ({ communication }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const typeLabel = COMMUNICATION_TYPE_LABELS[communication.communication_type] || communication.communication_type;
  const statusColor = STATUS_COLORS[communication.status] || STATUS_COLORS.draft;
  const isInternal = communication.status === 'internal' || communication.metadata?.internal_only;
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg p-4 mb-3 bg-white hover:shadow-sm transition-shadow">
        <CollapsibleTrigger asChild>
          <div className="flex items-start justify-between cursor-pointer">
            <div className="flex items-start gap-3 flex-1">
              <div className="mt-1">
                <StatusIcon status={communication.status} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900 truncate">
                    {communication.subject}
                  </span>
                  <Badge className={`text-xs ${statusColor}`}>
                    {communication.status === 'mocked' ? 'Sent (Test)' : communication.status}
                  </Badge>
                  {isInternal && (
                    <Badge variant="outline" className="text-xs">Internal Only</Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {communication.created_at ? format(new Date(communication.created_at), 'MMM d, yyyy h:mm a') : 'N/A'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {communication.recipient_email}
                  </span>
                </div>
                <Badge variant="secondary" className="mt-2 text-xs">
                  {typeLabel}
                </Badge>
              </div>
            </div>
            <Button variant="ghost" size="sm">
              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="mt-4 pt-4 border-t">
          {/* Internal Note */}
          {communication.internal_note && (
            <div className="mb-4 p-3 bg-purple-50 rounded-lg">
              <p className="text-sm font-medium text-purple-700 mb-1">Internal Note:</p>
              <p className="text-sm text-purple-900">{communication.internal_note}</p>
            </div>
          )}
          
          {/* Email Preview */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-2">
              <strong>From:</strong> {communication.sender_email}
            </div>
            <div className="text-sm text-gray-600 mb-2">
              <strong>To:</strong> {communication.recipient_name} &lt;{communication.recipient_email}&gt;
            </div>
            <div className="text-sm text-gray-600 mb-4">
              <strong>Subject:</strong> {communication.subject}
            </div>
            <div className="border-t pt-4">
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: communication.body_html }}
              />
            </div>
          </div>
          
          {/* Metadata */}
          {communication.metadata && Object.keys(communication.metadata).length > 0 && (
            <div className="mt-4 text-xs text-gray-500">
              <strong>Details:</strong>{' '}
              {communication.metadata.auto_reply && 'Auto-reply | '}
              {communication.metadata.stage_changed_to && `Stage changed to: ${communication.metadata.stage_changed_to} | `}
              {communication.template_used && `Template: ${communication.template_used}`}
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export const CommunicationHistory = ({ 
  recordType, 
  recordId, 
  candidateId = null,
  title = "Communication History",
  maxHeight = "400px",
  showRefresh = true
}) => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchCommunications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      let url;
      
      if (candidateId) {
        // Get all communications for candidate
        url = `${API}/admin/candidates/${candidateId}/communications`;
      } else {
        url = `${API}/admin/communications/${recordType}/${recordId}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch communications');
      
      const data = await response.json();
      setCommunications(data.communications || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if ((recordType && recordId) || candidateId) {
      fetchCommunications();
    }
  }, [recordType, recordId, candidateId]);
  
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-gray-500">
            Loading communications...
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-500 text-center py-4">
            Error: {error}
            <Button variant="link" onClick={fetchCommunications} className="ml-2">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail className="h-5 w-5" />
          {title}
          {communications.length > 0 && (
            <Badge variant="secondary" className="ml-2">{communications.length}</Badge>
          )}
        </CardTitle>
        {showRefresh && (
          <Button variant="outline" size="sm" onClick={fetchCommunications}>
            Refresh
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {communications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Mail className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No communications yet</p>
          </div>
        ) : (
          <div className="overflow-y-auto" style={{ maxHeight }}>
            {communications.map((comm) => (
              <CommunicationItem key={comm.communication_id} communication={comm} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CommunicationHistory;
